import { LinkButton } from '@/atoms/Button'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { AsDiv, AsH4, HeadingSM, LabelMD, ParagraphSM, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants'
import {
  CatalogImage,
  CatalogTitle,
  findTitleMoviePoster,
  findTitleProjectSlug,
  formatTitleFriendlyReleaseDate,
  formatWatchPath,
} from '@/services/ContentCatalog'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { isValidDate } from '@/utils/date-utils'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { TheatricalLandingReleaseOld } from './queries'
import { TitleRelease } from './types'

export function PastReleaseCard(props: { titleRelease: TitleRelease }) {
  const { title } = props.titleRelease
  const { t } = useTranslate('tickets')
  const { locale } = useLocale()
  const img = findTitleMoviePoster(title)
  const posterUrl = getCloudinaryImageUrl({ path: img?.cloudinaryPath, width: 385 })
  const watchPath = formatWatchPath(title)
  const learnMoreUrl = formatLearnMoreUrl(title)

  return (
    <li className="block w-full max-w-[960px] shrink-0">
      <div
        className="h-full overflow-hidden rounded-3xl bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${posterUrl})`,
        }}
      >
        <div className="relative inset-0 h-full grid-cols-[auto,408fr] items-center gap-[48px] px-10 py-8 backdrop-blur-3xl md:grid md:p-0 lg:gap-[98px]">
          {img && (
            <img
              className="mb-8 w-full rounded-xl shadow-light-5 md:mb-0 md:rounded-none 2lg:mb-auto 2lg:w-[366px]"
              style={{
                aspectRatio: formatAspectRatio(img),
              }}
              src={posterUrl}
              alt={title.title}
            />
          )}
          <div className="lg:py-[40px] lg:pr-[80px]">
            <HeadingSM
              as={AsH4}
              weight="bold"
              color="white"
              className="md:photon-heading-xl lg:photon-display mb-2 leading-[1.12em] md:text-[48px] 2lg:mb-4 2lg:text-[60px]"
            >
              {title.title}
            </HeadingSM>
            <TitleXS
              as={AsDiv}
              color="white"
              className="md:photon-title-md lg:photon-heading-xl mb-4 md:text-[18px] 2lg:text-[28px]"
            >
              {t('released', 'Released')}{' '}
              {formatTitleFriendlyReleaseDate(locale, title, {
                month: 'short',
                year: 'numeric',
              })}
            </TitleXS>
            <ParagraphSM
              color="white"
              className="lg:photon-paragraph-md mb-8 md:text-[18px] lg:text-[16px]"
              weight="normal"
            >
              {title.description.short}
            </ParagraphSM>

            <div className="flex flex-col items-start gap-4">
              {watchPath ? (
                <LinkButton
                  href={watchPath}
                  variant="white"
                  className="flex h-12 w-full items-center gap-[6px] whitespace-nowrap lg:w-fit 2lg:h-[56px]"
                >
                  <GuildIcon color1="black" color2="black" size={28} />
                  <LabelMD color="black" weight="semibold" className="2lg:text-[18px]">
                    {t('watchEarlyAccess', 'Watch Early Access')}
                  </LabelMD>
                </LinkButton>
              ) : null}

              {learnMoreUrl ? (
                <LinkButton
                  href={learnMoreUrl}
                  variant="core-gray-800"
                  className="flex h-12 w-full items-center gap-[6px] whitespace-nowrap lg:w-fit 2lg:h-[56px]"
                >
                  <LabelMD color="white" weight="semibold" className="text-white 2lg:text-[18px]">
                    {t('learnMore', 'Learn More')}
                  </LabelMD>
                </LinkButton>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </li>
  )
}

export function formatInTheatersLine(
  t: TranslateFunction,
  locale: string,
  release: TheatricalLandingReleaseOld,
): string | undefined {
  if (!release) return

  if (release.dateDisplayOption === 'full-date') {
    const releaseDate = release?.filmDetails?.theatricalReleaseDate
    if (!releaseDate) return

    const date = new Date(releaseDate)
    if (!isValidDate(date)) return

    const formatted = date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })
    return `${t('inTheaters', 'In Theaters')} ${formatted}`
    // NOTE: month-and-year included only for ease of migration. Current design/titles don't use it
  } else if (release.dateDisplayOption === 'year' || release.dateDisplayOption === 'month-and-year') {
    const year = release.filmDetails?.releaseYear
    return year ? `${t('comingIn', 'Coming in')} ${year}` : t('comingSoon', 'Coming Soon')
  }
}

function formatLearnMoreUrl(title: CatalogTitle): string | undefined {
  const slug = findTitleProjectSlug(title)
  return slug ? `${paths.movies.index}/${slug}` : undefined
}

function formatAspectRatio(img: CatalogImage): string {
  return img.aspect.split(':').join(' / ')
}
